/**
 * Centralized Session Manager
 * Ensures consistent session IDs across all services
 */

class SessionManager {
  private static instance: SessionManager;
  private sessionId: string | null = null;
  private roomId: string | null = null;
  private clientId: string | null = null;
  private sessionStartTime: Date | null = null;
  private callStartTime: Date | null = null;  // When bot actually joined the meeting
  private callEndTime: Date | null = null;    // When bot was disconnected

  private constructor() {}

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  /**
   * Initialize a new session with consistent IDs
   * Only initializes if there's no active session
   * If bot_id is provided, it will be used as the room_id
   */
  initializeSession(botId?: string): {
    sessionId: string;
    roomId: string;
    clientId: string;
    startTime: Date;
  } {
    // Check if session is already initialized in memory
    if (this.hasActiveSession()) {
      return {
        sessionId: this.sessionId!,
        roomId: this.roomId!,
        clientId: this.clientId!,
        startTime: this.sessionStartTime!
      };
    }

    // Skip session storage check - we want fresh sessions on navigation
    // Session storage is only useful within the same conversation page

    // Generate a single session ID that will be used everywhere
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    // Check if bot_id is provided (from Recall.ai bot creation)
    // If so, use it as the room ID for all services
    const storedBotId = botId || localStorage.getItem('salesguru_bot_id');
    if (storedBotId) {
      this.roomId = storedBotId;
    } else {
      // Fallback: Room ID is the same as session ID for consistency
      // This ensures all services group messages by the same identifier
      this.roomId = this.sessionId;
    }

    // Client ID can be unique per client
    this.clientId = `react-client-${this.getBrowserFingerprint()}`;

    this.sessionStartTime = new Date();

    return {
      sessionId: this.sessionId,
      roomId: this.roomId,
      clientId: this.clientId,
      startTime: this.sessionStartTime
    };
  }

  /**
   * Get current session info
   */
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      roomId: this.roomId,
      clientId: this.clientId,
      startTime: this.sessionStartTime,
      callStartTime: this.callStartTime,
      callEndTime: this.callEndTime
    };
  }

  /**
   * Set call start time when bot joins the meeting
   */
  setCallStartTime(time?: Date): void {
    this.callStartTime = time || new Date();
  }

  /**
   * Set call end time when bot is disconnected
   */
  setCallEndTime(time?: Date): void {
    this.callEndTime = time || new Date();
  }

  /**
   * Reset call timing (useful after saving a session to prepare for next call)
   */
  resetCallTiming(): void {
    this.callStartTime = null;
    this.callEndTime = null;
  }

  /**
   * Get call duration in seconds
   */
  getCallDuration(): number | null {
    if (!this.callStartTime || !this.callEndTime) {
      return null;
    }
    return Math.floor((this.callEndTime.getTime() - this.callStartTime.getTime()) / 1000);
  }

  /**
   * Get session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get room ID (same as session ID for consistency)
   */
  getRoomId(): string | null {
    return this.roomId;
  }

  /**
   * Get client ID
   */
  getClientId(): string | null {
    return this.clientId;
  }

  /**
   * Force a new session (clears existing and initializes new)
   * If bot_id is provided, it will be used as the room_id
   */
  forceNewSession(botId?: string): {
    sessionId: string;
    roomId: string;
    clientId: string;
    startTime: Date;
  } {
    this.clearSession();
    return this.initializeSession(botId);
  }

  /**
   * Set the room ID explicitly (useful when bot is created after session initialization)
   */
  setRoomId(roomId: string): void {
    this.roomId = roomId;
    localStorage.setItem('salesguru_bot_id', roomId);
  }

  /**
   * Clear session
   */
  clearSession(): void {
    this.sessionId = null;
    this.roomId = null;
    this.clientId = null;
    this.sessionStartTime = null;
    this.callStartTime = null;
    this.callEndTime = null;

    // Clear bot_id from localStorage
    localStorage.removeItem('salesguru_bot_id');

  }

  /**
   * Check if session is active
   */
  hasActiveSession(): boolean {
    return this.sessionId !== null;
  }

  private getBrowserFingerprint(): string {
    const userAgent = navigator.userAgent.replace(/\s+/g, '-').toLowerCase();
    const timestamp = Date.now().toString().slice(-6);
    return `${userAgent.slice(0, 10)}-${timestamp}`;
  }
}

export const sessionManager = SessionManager.getInstance();